import {
  BIRP_PATIENT_PARTICIPATION_SECTIONS,
  BIRP_PROGRESS_OPTIONS,
  BIRP_RESPONSE_TO_INTERVENTION_SECTIONS,
  BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS,
  BIRP_TOPICS_ADDRESSED_SECTIONS,
} from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/birp-note/BirpNoteConstant";
import { ExamCheckboxSection } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";
import CptCodesBlock from "../_shared/CptCodesBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import {
  CarePlanTemplateList,
  CodeList,
  OrderList,
  RenderTextWithContainerPattern,
  ReportContainer,
  ReportSection,
  TherapyNotesList,
  checkNull,
} from "../constant";
// Shared mapper for the MSE and Risk Assessment modals — the BIRP note reuses
// both of them (and their keys) from the Psychotherapy Note.
import InitialPsychModalsMapper from "../initialPsychEvolutionTemplate/components/InitialPsychModalsMapper";

interface BirpNoteTemplateProps {
  data: any;
}

// The checkbox modals persist each section as a comma-separated string of the
// selected option names, e.g. { topicsAddressed: "grief,sleepHygiene" }.
const parseSelected = (value: unknown): string[] =>
  typeof value === "string"
    ? value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

interface CheckboxSectionBlockProps {
  data: any;
  label: string;
  sections: ExamCheckboxSection[];
}

const CheckboxSectionBlock = ({
  data,
  label,
  sections,
}: CheckboxSectionBlockProps) => {
  const rendered = sections
    .map((section) => {
      const selected = parseSelected(data?.[section.key]);
      const labels = section.items
        .filter((it) => selected.includes(it.name))
        .map((it) => it.label);
      const note = (data?.[section.noteKey ?? `${section.key}Note`] ??
        "") as string;
      return { key: section.key, labels, note };
    })
    .filter((s) => s.labels.length > 0 || s.note.trim());

  if (!rendered.length) return null;

  return (
    <ReportContainer label={label}>
      {rendered.map((s) => (
        <div key={s.key} style={{ marginBottom: 8, paddingLeft: 5 }}>
          {s.labels.length > 0 && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              {s.labels.join(", ")}
            </p>
          )}
          {s.note.trim() && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              <strong>Note : </strong>
              {s.note}
            </p>
          )}
        </div>
      ))}
    </ReportContainer>
  );
};

// The BIRP form collects ICD codes under Intervention, so they are printed
// there rather than by the shared AssessmentBlock other note types use. Header
// and code alignment still follow the Psychiatric Progress Exam report, the
// same as every other note type's Assessment section.
const SelectedIcdCodesBlock = ({ data }: { data: any }) => {
  if (!checkNull(data?.assessmentCodes)) return null;

  return (
    <ReportContainer label="ICD Code">
      <CodeList codes={data?.assessmentCodes} />
    </ReportContainer>
  );
};

const ProgressBlock = ({ data }: { data: any }) => {
  const label = BIRP_PROGRESS_OPTIONS.find(
    (option) => option.value === data?.progress
  )?.label;

  if (!label) return null;

  return (
    <ReportContainer label="Progress">
      <p style={{ margin: "5px 0", paddingLeft: 5, fontSize: "14px" }}>
        {label}
      </p>
    </ReportContainer>
  );
};

/**
 * BIRP (Behavior, Intervention, Response, Plan) Progress Note report.
 *
 * The Chief Complaint is printed by <EncounterSummeryTemplate/> for every note
 * type, so it is not repeated here. Screening assessments are printed under
 * Behavior, the BIRP counterpart of the Objective section every other template
 * groups them under.
 */
const BirpNoteTemplate = ({ data }: BirpNoteTemplateProps) => {
  return (
    <>
      {/* ═══════════════ BEHAVIOR ═══════════════ */}
      <ReportSection label="Behavior">
        <RenderTextWithContainerPattern
          label="Behavior"
          val={data?.behaviorFreeNote}
        />
        <ScreeningAssessmentBlock data={data} />
        {/* MSE: checked options + per-section note + overall note. */}
        <InitialPsychModalsMapper data={data} type="mse" />
        {/* Risk Assessment: selected risk level + free-text note. */}
        <InitialPsychModalsMapper data={data} type="riskAssessment" />
      </ReportSection>

      {/* ═══════════════ INTERVENTION ═══════════════ */}
      <ReportSection label="Intervention">
        <RenderTextWithContainerPattern
          label="Intervention"
          val={data?.interventionFreeNote}
        />
        <CheckboxSectionBlock
          data={data}
          label="Therapeutic Interventions"
          sections={BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS}
        />
        <CheckboxSectionBlock
          data={data}
          label="Topics Addressed"
          sections={BIRP_TOPICS_ADDRESSED_SECTIONS}
        />
        <SelectedIcdCodesBlock data={data} />
      </ReportSection>

      {/* ═══════════════ RESPONSE ═══════════════ */}
      <ReportSection label="Response">
        <RenderTextWithContainerPattern
          label="Response"
          val={data?.responseFreeNote}
        />
        <CheckboxSectionBlock
          data={data}
          label="Patient Participation"
          sections={BIRP_PATIENT_PARTICIPATION_SECTIONS}
        />
        <CheckboxSectionBlock
          data={data}
          label="Response to Intervention"
          sections={BIRP_RESPONSE_TO_INTERVENTION_SECTIONS}
        />
        <ProgressBlock data={data} />
      </ReportSection>

      {/* ═══════════════ PLAN ═══════════════ */}
      <ReportSection label="Plan">
        <RenderTextWithContainerPattern label="Plan" val={data?.planFreeNote} />
        <PrescriptionsBlock data={data} />
        <VaccinesBlock data={data} />
        <OrderList orders={data?.patientOrders} />
        <CptCodesBlock data={data} />
        <RenderTextWithContainerPattern
          label="Care Plan"
          val={data?.carePlan}
        />
        <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
        <TherapyNotesList therapyNotes={data?.therapyNotes} />
        <RenderTextWithContainerPattern
          label="Instructions"
          val={data?.instruction}
        />
      </ReportSection>

      {/* ═══════════════ FOLLOW-UP ═══════════════
          Fed by the BIRP form's own Follow Up & Instructions field, and by the
          "Visit Note Template" button, which can push one into any note type.
          Printed the same way every other note template does. */}
      <ReportSection label="Follow-Up">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />
      </ReportSection>
    </>
  );
};

export default BirpNoteTemplate;
